import { Add, ArrowForward, Edit, WarningAmber } from '@mui/icons-material';
import { Alert, AlertTitle, Box, Checkbox, Chip, Divider, FormControl, Grid, InputLabel, Link, List, ListItem, ListItemText, MenuItem, Select, Stack, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import { Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AdvancedSearch from 'modules/common/advancedSearch.component';
import MaterialReactTable from 'material-react-table';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import Spinner from 'modules/common/spinner.component';

import {
  AUTH_UPDATE_IMPORT_SECTIONS,
  getAdditionalFields,
  getAuthUpdateImportColumnMappings,
  getAuthUpdateImportFieldByFieldId,
  getAuthUpdateImportFieldOrder,
  getAuthUpdateImportTemplateDisplayColumns,
  getCustomFieldListValues,
  getInitialAuthUpdateImportMappings,
  getInternalUserOptions,
  getVisibleSectionRows,
  hasOverrideValue,
  hasPosition,
} from './importAuthUpdates.tableDefinitions';
import {
  CLIENT_CUSTOM_FIELDS_STATUS,
  CLIENT_SEARCH_COLUMNS,
  CLIENT_SEARCH_COLUMN_EXTENSIONS,
  TabPanel,
  UploadTemplateFile,
  findClientCustomField,
  getClientCustomFieldDefinition,
  getClientCustomFieldDefinitions,
  getClientName,
  getImportErrorMessage,
  hasLookupItems,
  isActiveClientCustomField,
} from './importAuthFiles.components';
import { ImportTemplateEditor } from './importTemplateEditor.component';
import {
  authImportMappingTableProps,
  createClientCustomField,
  getAuthImportFieldLabel,
  getClientCustomFieldKey,
  getPositionError,
  getPositionValue,
  isClientCustomFieldKey,
  isClientCustomFieldMapping,
} from './importAuthFiles.tableDefinitions';

const useAuthUpdateImportStyles = makeStyles(theme => ({
  sectionCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    border: '1px solid #e0e0e0',
    borderRadius: 8,
  },
  mappingWorkspace: {
    minHeight: 420,
  },
  mappingSection: {
    padding: theme.spacing(1, 0, 2),
  },
  sectionHeader: {
    marginBottom: theme.spacing(1.5),
  },
}));

const AUTH_UPDATE_TEMPLATE = {
  templateId: 0,
  templateTypeId: 2,
  name: '',
  startingRow: 2,
  clientRecordId: null,
  mappings: [],
};

const AUTH_UPDATE_MAPPING = {
  authFileMappingId: 0,
  templateId: 0,
  fieldId: 0,
  position: null,
  overrideValue: null,
  customFieldCommonId: null,
};

const REQUIRED_TEMPLATE_NAME_ERROR = 'Template name is required';
const DATA_START_ROW_ERROR = 'Data start row is required and must be greater than zero';
const FILE_NUMBER_POSITION_ERROR = 'File Number must be mapped to a column';
const REQUIRED_MAPPING_ERROR = 'Enter a column position or an override value';
const ADDITIONAL_FIELD_REQUIRED_ERROR = 'Add at least one field besides File Number before saving.';
const FORM_ERROR_MESSAGE = 'Please fix the highlighted errors before saving.';
const AUTH_DETAILS_SECTION_MESSAGE = 'Division and Policy values are codes, and must belong to each authorization\'s client.';
const TEAM_SECTION_MESSAGE = 'Team member values must be the email address of the team member\'s internal user account.';
const SELECT_CLIENT_SECTION_MESSAGE = 'Select a client to map its standard and custom fields. Every authorization in the file must belong to this client.';
const LOADING_CUSTOM_FIELDS_SECTION_MESSAGE = 'Loading the client\'s custom fields...';
const CUSTOM_FIELDS_LOAD_ERROR_MESSAGE = 'Unable to load the client\'s custom fields.';
const UNAVAILABLE_CUSTOM_FIELDS_EDITOR_WARNING = 'Some custom field mappings are no longer available for this client. Remove or replace them before saving the template.';
const UNAVAILABLE_CUSTOM_FIELDS_SAVE_ERROR = 'Remove or replace the unavailable custom fields before saving.';
const UNAVAILABLE_CUSTOM_FIELDS_TEMPLATE_WARNING = 'Some custom field mappings are no longer available for this client. Edit the template to remove or replace them before running an import.';
const CUSTOM_FIELDS_TEMPLATE_LOAD_ERROR = 'Unable to load the custom fields for this template\'s client, so imports are unavailable. Reselect the template to try again.';

const EDITOR_SECTION_MESSAGES = {
  authDetails: AUTH_DETAILS_SECTION_MESSAGE,
  team: TEAM_SECTION_MESSAGE,
};

export const AUTH_UPDATE_IMPORT_TEMPLATE_TABS = {
  DRY_RUN_RESULTS: 'dryRunResults',
  IMPORT_RESULTS: 'importResults',
  MAPPING_DEFINITIONS: 'mappingDefinitions',
};

const fieldLabelSx = { color: '#777667', fontWeight: 400, fontSize: '1rem' };
const fieldValueSx = { color: '#43423a', fontWeight: 500, fontSize: '1rem' };
const placeholderValueSx = { fontStyle: 'italic', color: '#868686' };

const createAuthUpdateTemplate = () => ({
  ...AUTH_UPDATE_TEMPLATE,
  mappings: [],
});

const getAuthUpdateImportTemplateMappings = template => template?.mappings ?? [];

const getPositiveNumberError = (value, message) => {
  if (value === null || value === undefined || value === '') return message;

  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? null : message;
};

const getPositiveNumberValue = value => {
  if (value === '') return '';

  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : value;
};

const getTemplateFieldError = (field, value) => {
  switch (field) {
    case 'name':
      return value.trim() ? null : REQUIRED_TEMPLATE_NAME_ERROR;
    case 'startingRow':
      return getPositiveNumberError(value, DATA_START_ROW_ERROR);
    default:
      return null;
  }
};

const getTemplateErrors = template => ['name', 'startingRow'].reduce((errors, field) => {
  const error = getTemplateFieldError(field, template[field]);

  return error
    ? {
      ...errors,
      [field]: error,
    }
    : errors;
}, {});

const getVisibleAuthUpdateRows = (sections, selectedAdditionalFieldKeysBySection, mappingsByFieldKey) => sections.reduce((visibleRows, section) => [
  ...visibleRows,
  ...getVisibleSectionRows(section, selectedAdditionalFieldKeysBySection[section.sectionKey] || [], mappingsByFieldKey),
], []);

// File Number matches each row to its authorization, so it needs a column; every other field needs a column or an override
const getRequiredMappingErrors = visibleRows => visibleRows.reduce((errors, row) => {
  if (hasPosition(row)) return errors;
  if (row.isSystemRequired) return { ...errors, [row.fieldKey]: FILE_NUMBER_POSITION_ERROR };
  if (!hasOverrideValue(row)) return { ...errors, [row.fieldKey]: REQUIRED_MAPPING_ERROR };

  return errors;
}, {});

const getPositionValidationErrors = mappingsByFieldKey => Object.values(mappingsByFieldKey).reduce((errors, mapping) => (
  mapping.positionError
    ? {
      ...errors,
      [mapping.fieldKey]: mapping.positionError,
    }
    : errors
), {});

const hasErrors = errors => Object.keys(errors).length > 0;

export const getAuthUpdateImportTemplateId = template => template?.templateId || template?.id || template?.importTemplateId;

export const getAuthUpdateImportTemplateName = template => template?.name ?? template?.templateName ?? '';

export const getAuthUpdateImportTemplateList = response => {
  if (Array.isArray(response)) return response;

  return response?.templates ?? response?.data ?? [];
};

export const getAuthUpdateImportResultErrors = importResults => importResults?.errors ?? importResults?.data?.errors ?? [];

const getAuthUpdateImportResultAuthorizations = importResults => importResults?.authorizations ?? importResults?.data?.authorizations ?? [];

const getAuthorizationErrors = authorization => authorization?.errors ?? [];

// Rows that could not be fully saved carry their own errors, while file-level problems stop the whole import
export const hasAuthUpdateImportRowErrors = importResults => getAuthUpdateImportResultAuthorizations(importResults)
  .some(authorization => getAuthorizationErrors(authorization).length > 0);

const getDisplayValue = value => {
  if (value === null || value === undefined || value === '') return '';

  return String(value);
};

// A mapping takes its value from a column or from an override applied to every row, never both
const buildAuthUpdateTemplatePayload = (template, visibleRows) => ({
  ...template,
  mappings: visibleRows.map(row => ({
    ...AUTH_UPDATE_MAPPING,
    authFileMappingId: row.authFileMappingId ?? AUTH_UPDATE_MAPPING.authFileMappingId,
    templateId: row.templateId ?? template.templateId,
    fieldId: row.fieldId,
    position: hasPosition(row) ? row.position : null,
    overrideValue: hasPosition(row) || !hasOverrideValue(row) ? null : String(row.overrideValue).trim(),
    customFieldCommonId: row.customFieldCommonId ?? AUTH_UPDATE_MAPPING.customFieldCommonId,
  })),
});

const hydrateAuthUpdateTemplate = template => {
  const freshTemplate = createAuthUpdateTemplate();
  if (!template) return freshTemplate;

  return {
    ...freshTemplate,
    ...template,
    templateId: template.templateId ?? freshTemplate.templateId,
    templateTypeId: template.templateTypeId ?? freshTemplate.templateTypeId,
    name: getAuthUpdateImportTemplateName(template),
    startingRow: template.startingRow ?? freshTemplate.startingRow,
    clientRecordId: template.clientRecordId ?? freshTemplate.clientRecordId,
    mappings: getAuthUpdateImportTemplateMappings(template),
  };
};

const getMappingRowsByFieldKey = template => getAuthUpdateImportTemplateMappings(template).reduce((rowsByFieldKey, mapping) => {
  // Client custom field labels and types come from the client's fields once they load, so only the mapping state is kept here
  if (isClientCustomFieldMapping(mapping)) {
    const fieldKey = getClientCustomFieldKey(mapping.customFieldCommonId);

    return {
      ...rowsByFieldKey,
      [fieldKey]: {
        ...mapping,
        fieldKey,
        positionError: null,
      },
    };
  }

  const field = getAuthUpdateImportFieldByFieldId(mapping.fieldId);
  if (!field) return rowsByFieldKey;

  return {
    ...rowsByFieldKey,
    [field.fieldKey]: {
      ...field,
      ...mapping,
      fieldId: field.fieldId,
      fieldKey: field.fieldKey,
      fieldName: field.fieldName,
      isSystemRequired: field.isSystemRequired,
      positionError: null,
    },
  };
}, {});

const hydrateAuthUpdateMappings = template => ({
  ...getInitialAuthUpdateImportMappings(),
  ...getMappingRowsByFieldKey(template),
});

const getSelectedAdditionalFieldKeysBySection = template => {
  const fetchedRowsByFieldKey = getMappingRowsByFieldKey(template);

  return AUTH_UPDATE_IMPORT_SECTIONS.reduce((selectedBySection, section) => {
    const selectedFieldKeys = section.isClientCustomFieldSection
      ? Object.keys(fetchedRowsByFieldKey).filter(isClientCustomFieldKey)
      : section.fields.filter(field => (
        !field.isSystemRequired && fetchedRowsByFieldKey[field.fieldKey]
      )).map(field => field.fieldKey);

    return selectedFieldKeys.length > 0
      ? {
        ...selectedBySection,
        [section.sectionKey]: selectedFieldKeys,
      }
      : selectedBySection;
  }, {});
};

// Columns in sheet order, then overrides
const getPositionSortValue = row => (hasPosition(row) ? Number(row.position) : Number.MAX_SAFE_INTEGER);

// Standard fields follow the template editor (and Excel template) order. Custom fields have no fixed place, so they follow
// their columns, then overrides by name.
const compareMappingRows = (firstRow, secondRow) => {
  if (isClientCustomFieldMapping(firstRow) && isClientCustomFieldMapping(secondRow)) {
    return getPositionSortValue(firstRow) - getPositionSortValue(secondRow) || firstRow.fieldName.localeCompare(secondRow.fieldName);
  }

  return getAuthUpdateImportFieldOrder(firstRow.fieldId) - getAuthUpdateImportFieldOrder(secondRow.fieldId);
};

const getFieldOrderKey = (sectionKey, fieldName) => `${sectionKey}:${fieldName}`;

// Result fields are matched to the template's mappings by name (the API returns the same field names), so they can be listed in
// the same order as the Mapping Definitions tab
const getResultFieldOrder = mappingRows => new Map(mappingRows
  .filter(row => !row.isSectionHeader)
  .map((row, index) => [getFieldOrderKey(row.sectionKey, row.fieldName), index]));

const getOrderedResultFields = (section, fieldOrder) => {
  const getOrder = field => fieldOrder.get(getFieldOrderKey(section.sectionKey, field.fieldName)) ?? Number.MAX_SAFE_INTEGER;

  return [...(section.fields ?? [])].sort((firstField, secondField) => getOrder(firstField) - getOrder(secondField));
};

const getDisplayMappingRows = (template, clientCustomFields, clientCustomFieldsStatus, enumerations) => {
  const areCustomFieldsLoaded = clientCustomFieldsStatus === CLIENT_CUSTOM_FIELDS_STATUS.LOADED && hasLookupItems(enumerations, 'fieldStatuses');

  const mappingRows = getAuthUpdateImportTemplateMappings(template).reduce((rows, mapping) => {
    if (isClientCustomFieldMapping(mapping)) {
      const customField = findClientCustomField(clientCustomFields, mapping.customFieldCommonId);
      const definition = areCustomFieldsLoaded
        ? getClientCustomFieldDefinition(mapping.customFieldCommonId, customField, enumerations, !isActiveClientCustomField(customField, enumerations))
        : createClientCustomField({ customFieldCommonId: mapping.customFieldCommonId, fieldName: 'Custom field', dataType: '', isUnavailable: false });

      return [
        ...rows,
        {
          ...definition,
          ...mapping,
          id: `${mapping.authFileMappingId || definition.fieldKey}`,
        },
      ];
    }

    const field = getAuthUpdateImportFieldByFieldId(mapping.fieldId);
    if (!field) return rows;

    return [
      ...rows,
      {
        ...field,
        ...mapping,
        id: `${mapping.authFileMappingId || mapping.fieldId}`,
        fieldId: field.fieldId,
        fieldName: field.fieldName,
        isSystemRequired: field.isSystemRequired,
        dataType: field.dataType,
      },
    ];
  }, []);

  return AUTH_UPDATE_IMPORT_SECTIONS.reduce((displayRows, section) => {
    const sectionRows = mappingRows.filter(row => row.sectionKey === section.sectionKey).sort(compareMappingRows);

    if (sectionRows.length === 0) return displayRows;

    return [
      ...displayRows,
      {
        id: `section-${section.sectionKey}`,
        isSectionHeader: true,
        sectionKey: section.sectionKey,
        sectionName: section.sectionName,
      },
      ...sectionRows,
    ];
  }, []);
};

export const AuthUpdateTemplateSelector = props => {
  const { isLoading, onTemplateSelected, selectedTemplateId, templates } = props;

  return (
    <div>
      <FormControl sx={{ minWidth: 350 }} size="small">
        <InputLabel id="auth-update-template-label">
          Select Auth Update Import Template
        </InputLabel>
        <Select
          labelId="auth-update-template-label"
          id="auth-update-template-select"
          label="Select Auth Update Import Template"
          disabled={isLoading}
          value={selectedTemplateId}
          onChange={event => onTemplateSelected(event.target.value)}
        >
          <MenuItem value="">Select a template</MenuItem>
          {templates.map(template => (
            <MenuItem key={getAuthUpdateImportTemplateId(template)} value={String(getAuthUpdateImportTemplateId(template))}>
              {getAuthUpdateImportTemplateName(template)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};

AuthUpdateTemplateSelector.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  onTemplateSelected: PropTypes.func.isRequired,
  selectedTemplateId: PropTypes.string.isRequired,
  templates: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export const AuthUpdateTemplateEditorButton = props => {
  const { disabled, isEditMode, onClick } = props;
  const Icon = isEditMode ? Edit : Add;

  return (
    <Button variant="contained" color="primary" startIcon={<Icon />} onClick={onClick} disabled={disabled}>
      {isEditMode ? 'Edit Template' : 'Create Auth Update Template'}
    </Button>
  );
};

AuthUpdateTemplateEditorButton.defaultProps = {
  disabled: false,
};

AuthUpdateTemplateEditorButton.propTypes = {
  disabled: PropTypes.bool,
  isEditMode: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

const AuthUpdateImportSectionTable = props => {
  const {
    classes,
    enumerations,
    headerActions,
    internalUserOptions,
    isFieldSelectionDisabled,
    mappingsByFieldKey,
    onAdditionalFieldsChange,
    onOverrideValueChange,
    onPositionChange,
    section,
    sectionMessage,
    selectedAdditionalFieldKeys,
  } = props;
  const visibleRows = getVisibleSectionRows(section, selectedAdditionalFieldKeys, mappingsByFieldKey);
  const additionalFields = getAdditionalFields(section);
  const showFieldSelector = additionalFields.length > 0 || Boolean(headerActions);
  const columns = useMemo(() => getAuthUpdateImportColumnMappings({
    enumerations,
    internalUserOptions,
    onOverrideValueChange,
    onPositionChange,
  }), [enumerations, internalUserOptions, onOverrideValueChange, onPositionChange]);

  return (
    <Box className={classes.mappingSection}>
      <Stack className={classes.sectionHeader} direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
        <Box>
          <Typography variant="h6" gutterBottom>
            {section.sectionName}
          </Typography>
          {sectionMessage && (
            <Typography variant="body2" color="textSecondary">
              {sectionMessage}
            </Typography>
          )}
        </Box>
        {showFieldSelector && (
          <Stack direction="row" spacing={2} alignItems="center">
            {headerActions}
            <FormControl size="small" sx={{ minWidth: 280 }} disabled={isFieldSelectionDisabled || additionalFields.length === 0}>
              <Select
                multiple
                displayEmpty
                value={selectedAdditionalFieldKeys}
                renderValue={selectedFieldKeys => (
                  selectedFieldKeys.length === 0
                    ? 'Add additional fields'
                    : `${selectedFieldKeys.length} additional field${selectedFieldKeys.length === 1 ? '' : 's'} selected`
                )}
                onChange={event => onAdditionalFieldsChange(section.sectionKey, event.target.value)}
              >
                {additionalFields.map(field => (
                  <MenuItem key={field.fieldKey} value={field.fieldKey}>
                    <Checkbox checked={selectedAdditionalFieldKeys.includes(field.fieldKey)} color="primary" size="small" />
                    <ListItemText
                      primary={getAuthImportFieldLabel(field)}
                      primaryTypographyProps={field.isUnavailable ? { color: 'error' } : undefined}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        )}
      </Stack>
      {visibleRows.length > 0 && (
        <MaterialReactTable
          data={visibleRows}
          columns={columns}
          getRowId={row => row.fieldKey}
          {...authImportMappingTableProps}
        />
      )}
    </Box>
  );
};

AuthUpdateImportSectionTable.propTypes = {
  classes: PropTypes.object.isRequired,
  enumerations: PropTypes.object,
  headerActions: PropTypes.node,
  internalUserOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  isFieldSelectionDisabled: PropTypes.bool,
  // Client custom field entries only hold mapping state; their labels come from the section's field definitions
  mappingsByFieldKey: PropTypes.objectOf(PropTypes.shape({
    fieldKey: PropTypes.string,
    overrideValue: PropTypes.string,
    position: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    positionError: PropTypes.string,
  })).isRequired,
  onAdditionalFieldsChange: PropTypes.func.isRequired,
  onOverrideValueChange: PropTypes.func.isRequired,
  onPositionChange: PropTypes.func.isRequired,
  section: PropTypes.shape({
    fields: PropTypes.arrayOf(PropTypes.object).isRequired,
    sectionKey: PropTypes.string.isRequired,
    sectionName: PropTypes.string.isRequired,
  }).isRequired,
  sectionMessage: PropTypes.node,
  selectedAdditionalFieldKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
};

AuthUpdateImportSectionTable.defaultProps = {
  enumerations: null,
  headerActions: null,
  isFieldSelectionDisabled: false,
  sectionMessage: '',
};

export const AuthUpdateTemplateEditor = props => {
  const { clients, enumerations, initialTemplate, internalUsers, isEditMode, onClose, onLoadClientCustomFields, onSaveTemplate, open } = props;
  const classes = useAuthUpdateImportStyles();
  const [template, setTemplate] = useState(createAuthUpdateTemplate);
  const [templateErrors, setTemplateErrors] = useState({});
  const [mappingsByFieldKey, setMappingsByFieldKey] = useState(getInitialAuthUpdateImportMappings);
  const [selectedAdditionalFieldKeysBySection, setSelectedAdditionalFieldKeysBySection] = useState({});
  const [clientCustomFields, setClientCustomFields] = useState([]);
  const [clientCustomFieldsStatus, setClientCustomFieldsStatus] = useState(CLIENT_CUSTOM_FIELDS_STATUS.IDLE);
  const [additionalFieldError, setAdditionalFieldError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const templateClientRecordId = template.clientRecordId;
  const hasPositionErrors = Object.values(mappingsByFieldKey).some(mapping => Boolean(mapping.positionError));
  const selectedCustomFieldKeys = selectedAdditionalFieldKeysBySection.customFields;
  const areCustomFieldsReady = clientCustomFieldsStatus === CLIENT_CUSTOM_FIELDS_STATUS.LOADED && hasLookupItems(enumerations, 'fieldStatuses');
  // Without the client's fields the custom section cannot render its mappings, so saving would drop them
  const isCustomFieldSavePending = Boolean(selectedCustomFieldKeys?.length) && !areCustomFieldsReady;
  const internalUserOptions = useMemo(() => getInternalUserOptions(internalUsers), [internalUsers]);

  const editorSections = useMemo(() => {
    const customFieldDefinitions = areCustomFieldsReady
      ? getClientCustomFieldDefinitions(clientCustomFields, enumerations, selectedCustomFieldKeys || []).map(field => ({
        ...field,
        listValues: getCustomFieldListValues(findClientCustomField(clientCustomFields, field.customFieldCommonId)),
      }))
      : [];

    return AUTH_UPDATE_IMPORT_SECTIONS.map(section => (
      section.isClientCustomFieldSection
        ? { ...section, fields: customFieldDefinitions }
        : section
    ));
  }, [areCustomFieldsReady, clientCustomFields, enumerations, selectedCustomFieldKeys]);

  const hasUnavailableCustomFields = editorSections.some(section => section.fields.some(field => field.isUnavailable));

  React.useEffect(() => {
    if (!open) return;

    setTemplate(isEditMode ? hydrateAuthUpdateTemplate(initialTemplate) : createAuthUpdateTemplate());
    setTemplateErrors({});
    setMappingsByFieldKey(isEditMode ? hydrateAuthUpdateMappings(initialTemplate) : getInitialAuthUpdateImportMappings());
    setSelectedAdditionalFieldKeysBySection(isEditMode ? getSelectedAdditionalFieldKeysBySection(initialTemplate) : {});
    setAdditionalFieldError('');
  }, [initialTemplate, isEditMode, open]);

  React.useEffect(() => {
    if (!open || !templateClientRecordId) {
      setClientCustomFields([]);
      setClientCustomFieldsStatus(CLIENT_CUSTOM_FIELDS_STATUS.IDLE);
      return undefined;
    }

    let isCurrentClient = true;
    setClientCustomFieldsStatus(CLIENT_CUSTOM_FIELDS_STATUS.LOADING);

    onLoadClientCustomFields(templateClientRecordId).then(customFields => {
      if (!isCurrentClient) return;

      setClientCustomFields(customFields ?? []);
      setClientCustomFieldsStatus(customFields ? CLIENT_CUSTOM_FIELDS_STATUS.LOADED : CLIENT_CUSTOM_FIELDS_STATUS.ERROR);
    });

    return () => {
      isCurrentClient = false;
    };
  }, [onLoadClientCustomFields, open, templateClientRecordId]);

  const updateTemplateValue = (field, value) => {
    setTemplate(currentTemplate => ({
      ...currentTemplate,
      [field]: value,
    }));

    setTemplateErrors(currentErrors => {
      if (!currentErrors[field]) return currentErrors;

      const error = getTemplateFieldError(field, value);
      const nextErrors = { ...currentErrors };

      if (error) {
        nextErrors[field] = error;
      } else {
        delete nextErrors[field];
      }

      return nextErrors;
    });
  };

  // A column position replaces the override value
  const handlePositionChange = React.useCallback((fieldKey, value) => {
    const position = getPositionValue(value);

    setMappingsByFieldKey(currentMappings => ({
      ...currentMappings,
      [fieldKey]: {
        ...currentMappings[fieldKey],
        fieldKey,
        position,
        positionError: getPositionError(value),
        overrideValue: position !== null ? null : currentMappings[fieldKey]?.overrideValue ?? null,
      },
    }));
  }, []);

  // An override value replaces the column position (and any error on it)
  const handleOverrideValueChange = React.useCallback((fieldKey, value) => {
    const overrideValue = value === '' ? null : value;

    setMappingsByFieldKey(currentMappings => ({
      ...currentMappings,
      [fieldKey]: {
        ...currentMappings[fieldKey],
        fieldKey,
        overrideValue,
        position: overrideValue !== null ? null : currentMappings[fieldKey]?.position ?? null,
        positionError: overrideValue !== null ? null : currentMappings[fieldKey]?.positionError ?? null,
      },
    }));
  }, []);

  const handleClientChange = selectedClientRecordId => {
    // The search clears with an empty array
    const clientRecordId = selectedClientRecordId && !Array.isArray(selectedClientRecordId) ? selectedClientRecordId : null;
    if (clientRecordId === templateClientRecordId) return;

    updateTemplateValue('clientRecordId', clientRecordId);

    // Custom field mappings belong to the previous client's fields
    setSelectedAdditionalFieldKeysBySection(currentSelections => ({
      ...currentSelections,
      customFields: [],
    }));
    setMappingsByFieldKey(currentMappings => Object.fromEntries(
      Object.entries(currentMappings).filter(([fieldKey]) => !isClientCustomFieldKey(fieldKey)),
    ));
  };

  const handleAdditionalFieldsChange = (sectionKey, nextSelectedFieldKeys) => {
    const selectedFieldKeys = Array.isArray(nextSelectedFieldKeys) ? nextSelectedFieldKeys : [];
    const removedFieldKeys = (selectedAdditionalFieldKeysBySection[sectionKey] || []).filter(fieldKey => !selectedFieldKeys.includes(fieldKey));

    setSelectedAdditionalFieldKeysBySection(currentSelections => ({
      ...currentSelections,
      [sectionKey]: selectedFieldKeys,
    }));

    if (selectedFieldKeys.length > 0) {
      setAdditionalFieldError('');
    }

    if (removedFieldKeys.length === 0) return;

    setMappingsByFieldKey(currentMappings => removedFieldKeys.reduce((nextMappings, fieldKey) => ({
      ...nextMappings,
      [fieldKey]: {
        ...nextMappings[fieldKey],
        position: null,
        positionError: null,
        overrideValue: null,
      },
    }), currentMappings));
  };

  const resetEditor = () => {
    setTemplate(createAuthUpdateTemplate());
    setTemplateErrors({});
    setMappingsByFieldKey(getInitialAuthUpdateImportMappings());
    setSelectedAdditionalFieldKeysBySection({});
    setAdditionalFieldError('');
  };

  const handleClose = (...args) => {
    resetEditor();
    onClose(...args);
  };

  const handleSave = async () => {
    if (isSaving || hasUnavailableCustomFields || isCustomFieldSavePending) return;

    const visibleRows = getVisibleAuthUpdateRows(editorSections, selectedAdditionalFieldKeysBySection, mappingsByFieldKey);
    const hasAdditionalField = visibleRows.some(row => !row.isSystemRequired);
    const nextTemplateErrors = getTemplateErrors(template);
    const nextPositionErrors = {
      ...getPositionValidationErrors(mappingsByFieldKey),
      ...getRequiredMappingErrors(visibleRows),
    };

    setTemplateErrors(nextTemplateErrors);
    setAdditionalFieldError(hasAdditionalField ? '' : ADDITIONAL_FIELD_REQUIRED_ERROR);
    setMappingsByFieldKey(currentMappings => Object.entries(nextPositionErrors).reduce((nextMappings, [fieldKey, error]) => ({
      ...nextMappings,
      [fieldKey]: {
        ...nextMappings[fieldKey],
        positionError: error,
      },
    }), currentMappings));

    if (hasErrors(nextTemplateErrors) || hasErrors(nextPositionErrors) || !hasAdditionalField) return;

    setIsSaving(true);

    const wasSaved = await onSaveTemplate(buildAuthUpdateTemplatePayload(template, visibleRows));

    setIsSaving(false);

    if (wasSaved !== false) {
      handleClose();
    }
  };

  const getFormErrorMessage = () => {
    if (hasUnavailableCustomFields) return UNAVAILABLE_CUSTOM_FIELDS_SAVE_ERROR;
    if (hasErrors(templateErrors) || hasPositionErrors) return FORM_ERROR_MESSAGE;
    return additionalFieldError;
  };

  const getCustomFieldsSectionMessage = () => {
    if (!templateClientRecordId) return SELECT_CLIENT_SECTION_MESSAGE;
    if (clientCustomFieldsStatus === CLIENT_CUSTOM_FIELDS_STATUS.ERROR) return CUSTOM_FIELDS_LOAD_ERROR_MESSAGE;
    if (!areCustomFieldsReady) return LOADING_CUSTOM_FIELDS_SECTION_MESSAGE;
    return '';
  };

  const formErrorMessage = getFormErrorMessage();
  const editorSectionMessages = {
    ...EDITOR_SECTION_MESSAGES,
    customFields: getCustomFieldsSectionMessage(),
  };

  const clientSearch = (
    <Box sx={{ width: 280 }}>
      <AdvancedSearch
        labelText="Client"
        displayValueAccessorKey="shortName"
        saveIdValueAccessorKey="clientRecordId"
        searchingCriteria={CLIENT_SEARCH_COLUMNS}
        searchingCriteriaExtensions={CLIENT_SEARCH_COLUMN_EXTENSIONS}
        searchingRows={clients}
        value={getClientName(clients, templateClientRecordId)}
        id="auth-update-import-template-client"
        setIdValue={handleClientChange}
        size="small"
        fullWidth
      />
    </Box>
  );

  return (
    <ImportTemplateEditor
      open={open}
      onClose={handleClose}
      disableEnforceFocus
      title={isEditMode ? 'Edit Auth Update Import Template' : 'Create Auth Update Import Template'}
      secondaryText="Map the File Number column and the authorization fields to update. Blank cells leave an authorization's current value unchanged."
      actions={(
        <Stack direction="row" spacing={2} justifyContent="flex-end" alignItems="center">
          {formErrorMessage && (
            <Typography color="error" variant="body2">
              {formErrorMessage}
            </Typography>
          )}
          <Button onClick={handleClose} disabled={isSaving}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={hasPositionErrors || hasUnavailableCustomFields || isCustomFieldSavePending || isSaving}
            onClick={handleSave}
            startIcon={isSaving ? <Spinner size="1x" color="inherit" /> : null}
          >
            {isSaving ? 'Saving...' : isEditMode ? 'Update Template' : 'Save Template'}
          </Button>
        </Stack>
      )}
    >
      <Box className={classes.sectionCard}>
        <Typography variant="h6" gutterBottom>
          Template Details
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Template Name"
              variant="outlined"
              size="small"
              required
              error={Boolean(templateErrors.name)}
              helperText={templateErrors.name || ''}
              value={template.name}
              onChange={event => updateTemplateValue('name', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Data Start Row"
              variant="outlined"
              size="small"
              required
              error={Boolean(templateErrors.startingRow)}
              helperText={templateErrors.startingRow || ''}
              value={template.startingRow}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              onChange={event => updateTemplateValue('startingRow', getPositiveNumberValue(event.target.value))}
            />
          </Grid>
        </Grid>
      </Box>

      <Box className={`${classes.sectionCard} ${classes.mappingWorkspace}`}>
        {hasUnavailableCustomFields && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {UNAVAILABLE_CUSTOM_FIELDS_EDITOR_WARNING}
          </Alert>
        )}
        {editorSections.map((section, index) => (
          <React.Fragment key={section.sectionKey}>
            {index > 0 && <Divider />}
            <AuthUpdateImportSectionTable
              classes={classes}
              enumerations={enumerations}
              headerActions={section.isClientCustomFieldSection ? clientSearch : null}
              internalUserOptions={internalUserOptions}
              isFieldSelectionDisabled={section.isClientCustomFieldSection && !areCustomFieldsReady}
              mappingsByFieldKey={mappingsByFieldKey}
              section={section}
              sectionMessage={editorSectionMessages[section.sectionKey] || ''}
              selectedAdditionalFieldKeys={selectedAdditionalFieldKeysBySection[section.sectionKey] || []}
              onAdditionalFieldsChange={handleAdditionalFieldsChange}
              onOverrideValueChange={handleOverrideValueChange}
              onPositionChange={handlePositionChange}
            />
          </React.Fragment>
        ))}
      </Box>
    </ImportTemplateEditor>
  );
};

AuthUpdateTemplateEditor.propTypes = {
  clients: PropTypes.arrayOf(PropTypes.object),
  enumerations: PropTypes.object,
  initialTemplate: PropTypes.object,
  internalUsers: PropTypes.arrayOf(PropTypes.object),
  isEditMode: PropTypes.bool.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // Resolves to the client's custom fields, or null when they could not be loaded
  onLoadClientCustomFields: PropTypes.func.isRequired,
  onSaveTemplate: PropTypes.func.isRequired,
};

AuthUpdateTemplateEditor.defaultProps = {
  clients: [],
  enumerations: null,
  initialTemplate: null,
  internalUsers: [],
};

const AuthUpdatePlaceholderValue = props => {
  const { children } = props;

  return <Box component="span" sx={placeholderValueSx}>{children}</Box>;
};

AuthUpdatePlaceholderValue.propTypes = {
  children: PropTypes.node.isRequired,
};

// Shows a field the way the authorization details header card does: an empty original value reads "Empty" and a blank cell "Not Modified"
export const AuthUpdateFieldChange = props => {
  const { field, showOriginalValue } = props;
  const hasOriginalValue = field.originalValue !== null && field.originalValue !== undefined && field.originalValue !== '';
  const hasNewValue = field.newValue !== null && field.newValue !== undefined;

  return (
    <Grid item xs={12} sm={6} md={3}>
      <Box mb={0.5}>
        <Typography variant="subtitle2" sx={fieldLabelSx}>
          {field.fieldName}
        </Typography>
      </Box>
      <Typography variant="body2" component="div" sx={{ ...fieldValueSx, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
        {showOriginalValue && (
          <>
            {hasOriginalValue ? field.originalValue : <AuthUpdatePlaceholderValue>Empty</AuthUpdatePlaceholderValue>}
            <ArrowForward fontSize="inherit" sx={{ color: '#868686' }} />
          </>
        )}
        {hasNewValue ? field.newValue : <AuthUpdatePlaceholderValue>Not Modified</AuthUpdatePlaceholderValue>}
      </Typography>
    </Grid>
  );
};

AuthUpdateFieldChange.propTypes = {
  field: PropTypes.shape({
    fieldName: PropTypes.string.isRequired,
    newValue: PropTypes.string,
    originalValue: PropTypes.string,
  }).isRequired,
  showOriginalValue: PropTypes.bool,
};

AuthUpdateFieldChange.defaultProps = {
  showOriginalValue: true,
};

const AuthUpdateImportRowDetails = props => {
  const { authorization, fieldOrder, isSubmission } = props;
  const errors = getAuthorizationErrors(authorization);
  const sections = authorization.sections ?? [];

  return (
    <Stack spacing={2} sx={{ width: '100%', py: 1 }}>
      {errors.length > 0 && (
        <Alert severity="error">
          <AlertTitle>Errors</AlertTitle>
          <List dense disablePadding>
            {errors.map((error, index) => (
              <ListItem key={`${getImportErrorMessage(error)}-${index}`} disableGutters>
                <Typography variant="body2">{getImportErrorMessage(error)}</Typography>
              </ListItem>
            ))}
          </List>
        </Alert>
      )}
      {sections.map((section, index) => (
        <Box key={section.sectionKey}>
          {index > 0 && <Divider sx={{ mb: 2 }} />}
          <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {section.sectionName}
            </Typography>
            {section.isSaved === false && <Chip label="Not Saved" color="error" size="small" variant="outlined" />}
          </Stack>
          <Grid container spacing={2}>
            {getOrderedResultFields(section, fieldOrder).map((field, fieldIndex) => (
              <AuthUpdateFieldChange key={`${field.fieldName}-${fieldIndex}`} field={field} showOriginalValue={!isSubmission} />
            ))}
          </Grid>
        </Box>
      ))}
    </Stack>
  );
};

AuthUpdateImportRowDetails.propTypes = {
  authorization: PropTypes.shape({
    errors: PropTypes.arrayOf(PropTypes.string),
    sections: PropTypes.arrayOf(PropTypes.shape({
      fields: PropTypes.arrayOf(PropTypes.object),
      isSaved: PropTypes.bool,
      sectionKey: PropTypes.string.isRequired,
      sectionName: PropTypes.string.isRequired,
    })),
  }).isRequired,
  // Section key and field name to the field's place in the template's mappings (see getResultFieldOrder)
  fieldOrder: PropTypes.instanceOf(Map).isRequired,
  isSubmission: PropTypes.bool.isRequired,
};

const AuthUpdateFileNumberCell = props => {
  const { authorization } = props;
  const errorCount = getAuthorizationErrors(authorization).length;

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Link href={`/authorizations/${authorization.fileId}`} target="_blank" rel="noopener noreferrer" underline="hover">
        {authorization.fileNumber}
      </Link>
      {errorCount > 0 && (
        <Tooltip title={`${errorCount} error${errorCount === 1 ? '' : 's'}. Expand the row to see what was not saved.`}>
          <WarningAmber color="warning" fontSize="small" />
        </Tooltip>
      )}
    </Stack>
  );
};

AuthUpdateFileNumberCell.propTypes = {
  authorization: PropTypes.shape({
    errors: PropTypes.arrayOf(PropTypes.string),
    fileId: PropTypes.number.isRequired,
    fileNumber: PropTypes.string.isRequired,
  }).isRequired,
};

const authUpdateImportResultColumns = [
  {
    accessorKey: 'fileNumber',
    header: 'File Number',
    Cell: ({ row }) => <AuthUpdateFileNumberCell authorization={row.original} />,
  },
  { accessorKey: 'name', header: 'Name' },
  { accessorKey: 'fileType', header: 'File Type' },
  { accessorKey: 'client', header: 'Client' },
  { accessorKey: 'relocationCounselor', header: 'Relocation Counselor' },
  { accessorKey: 'changedFieldCount', header: 'Changes' },
];

// Fields the row changes. After a submission, a section that was not saved changed nothing.
const getChangedFieldCount = (authorization, isSubmission) => (authorization.sections ?? [])
  .filter(section => !isSubmission || section.isSaved !== false)
  .reduce((count, section) => count + (section.fields ?? []).filter(field => field.isModified).length, 0);

const AuthUpdateImportResults = props => {
  const { emptyMessage, errorTitle, fieldOrder, importResults, isSubmission, successMessage } = props;

  if (!importResults) {
    return (
      <Box pt={2}>
        <Typography variant="body2" color="textSecondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  const errors = getAuthUpdateImportResultErrors(importResults);
  if (errors.length > 0) {
    return (
      <Box pt={2}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorTitle} {errors.length} issue{errors.length === 1 ? '' : 's'}.
        </Alert>
        <List dense>
          {errors.map((error, index) => (
            <ListItem key={`${getImportErrorMessage(error)}-${index}`}>
              <Typography variant="body2">{getImportErrorMessage(error)}</Typography>
            </ListItem>
          ))}
        </List>
      </Box>
    );
  }

  const authorizations = getAuthUpdateImportResultAuthorizations(importResults).map((authorization, index) => ({
    ...authorization,
    id: `authorization-${authorization.fileId ?? index}`,
    changedFieldCount: getChangedFieldCount(authorization, isSubmission),
  }));
  const authorizationsWithErrors = authorizations.filter(authorization => getAuthorizationErrors(authorization).length > 0).length;
  const totalChangedFieldCount = authorizations.reduce((total, authorization) => total + authorization.changedFieldCount, 0);

  return (
    <Box pt={2}>
      <Typography variant="body2" color="textSecondary" mb={2}>
        {successMessage}
      </Typography>
      {authorizationsWithErrors > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {authorizationsWithErrors} authorization{authorizationsWithErrors === 1 ? ' was' : 's were'} not fully updated. Expand the rows marked with a warning to see what was not saved.
        </Alert>
      )}
      <Stack direction="row" spacing={4} mb={2}>
        <Box>
          <Typography variant="body2" color="textSecondary">Total Authorizations</Typography>
          <Typography variant="h6">{authorizations.length}</Typography>
        </Box>
        <Box>
          <Typography variant="body2" color="textSecondary">Total Fields Changed</Typography>
          <Typography variant="h6">{totalChangedFieldCount}</Typography>
        </Box>
        {isSubmission && (
          <Box>
            <Typography variant="body2" color="textSecondary">Authorizations With Errors</Typography>
            <Typography variant="h6">{authorizationsWithErrors}</Typography>
          </Box>
        )}
      </Stack>
      <MaterialReactTable
        data={authorizations}
        columns={authUpdateImportResultColumns}
        getRowId={row => row.id}
        enablePagination={false}
        enableBottomToolbar={false}
        enableTopToolbar={false}
        enableColumnActions={false}
        enableColumnFilters={false}
        enableGlobalFilter={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableHiding={false}
        renderDetailPanel={({ row }) => (
          <AuthUpdateImportRowDetails authorization={row.original} fieldOrder={fieldOrder} isSubmission={isSubmission} />
        )}
      />
    </Box>
  );
};

AuthUpdateImportResults.propTypes = {
  emptyMessage: PropTypes.string.isRequired,
  errorTitle: PropTypes.string.isRequired,
  fieldOrder: PropTypes.instanceOf(Map).isRequired,
  importResults: PropTypes.object,
  isSubmission: PropTypes.bool,
  successMessage: PropTypes.string.isRequired,
};

AuthUpdateImportResults.defaultProps = {
  importResults: null,
  isSubmission: false,
};

export const SelectedAuthUpdateImportTemplate = props => {
  const {
    activeTab,
    clientCustomFields,
    clientCustomFieldsStatus,
    clients,
    dryRunResults,
    enumerations,
    importResults,
    internalUsers,
    isDryRunLoading,
    isImportSubmitting,
    isLoading,
    onDryRun,
    onFileSelected,
    onSubmitImport,
    onTabChange,
    selectedTemplateFile,
    template,
  } = props;
  const classes = useAuthUpdateImportStyles();
  const templateDisplayColumns = useMemo(() => getAuthUpdateImportTemplateDisplayColumns(internalUsers), [internalUsers]);

  if (isLoading) {
    return (
      <Box className={classes.sectionCard}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Spinner size="1x" />
          <Typography variant="body2">Loading auth update import template...</Typography>
        </Stack>
      </Box>
    );
  }

  if (!template) return null;

  const mappingRows = getDisplayMappingRows(template, clientCustomFields, clientCustomFieldsStatus, enumerations);
  const resultFieldOrder = getResultFieldOrder(mappingRows);
  const hasCustomFieldMappings = getAuthUpdateImportTemplateMappings(template).some(isClientCustomFieldMapping);
  const hasUnavailableCustomFields = mappingRows.some(row => row.isUnavailable);
  const hasCustomFieldLoadError = hasCustomFieldMappings && clientCustomFieldsStatus === CLIENT_CUSTOM_FIELDS_STATUS.ERROR;
  // Imports stay blocked until the mapped custom fields are confirmed to still be available
  const isImportBlocked = hasCustomFieldMappings && (hasUnavailableCustomFields || clientCustomFieldsStatus !== CLIENT_CUSTOM_FIELDS_STATUS.LOADED);
  const hasDryRunErrors = getAuthUpdateImportResultErrors(dryRunResults).length > 0;
  const canRunDryRun = Boolean(selectedTemplateFile && !isDryRunLoading && !isImportSubmitting && !isImportBlocked);
  const canSubmitImport = Boolean(selectedTemplateFile && dryRunResults && !hasDryRunErrors && !isDryRunLoading && !isImportSubmitting && !isImportBlocked);

  return (
    <>
      <Box className={classes.sectionCard}>
        <Grid container rowSpacing={1} columnSpacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>
              Template Details
            </Typography>
          </Grid>
          <Grid item xs={12} md={8}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end">
              <UploadTemplateFile
                key={getAuthUpdateImportTemplateId(template)}
                clearTitle="Clear Auth Update File"
                uploadLabel="Upload Auth Update File"
                onFileSelected={onFileSelected}
              />
              <Button
                variant="contained"
                color="primary"
                disabled={!canRunDryRun}
                onClick={onDryRun}
                startIcon={isDryRunLoading ? <Spinner size="1x" color="inherit" /> : null}
              >
                {isDryRunLoading ? 'Running...' : 'Dry Run'}
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={!canSubmitImport}
                onClick={onSubmitImport}
                startIcon={isImportSubmitting ? <Spinner size="1x" color="inherit" /> : null}
              >
                {isImportSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
            </Stack>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="textSecondary">Template Name</Typography>
            <Typography variant="body1">{getAuthUpdateImportTemplateName(template)}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="textSecondary">Client</Typography>
            <Typography variant="body1">{template.clientRecordId ? getClientName(clients, template.clientRecordId) : 'Any client'}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="textSecondary">Data Start Row</Typography>
            <Typography variant="body1">{getDisplayValue(template.startingRow)}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="textSecondary">Selected File</Typography>
            <Typography variant="body1">{selectedTemplateFile?.name ?? ''}</Typography>
          </Grid>
        </Grid>
      </Box>
      <Box className={classes.sectionCard}>
        <Tabs value={activeTab} onChange={(event, nextTab) => onTabChange(nextTab)} indicatorColor="primary" textColor="primary">
          <Tab value={AUTH_UPDATE_IMPORT_TEMPLATE_TABS.MAPPING_DEFINITIONS} label="Mapping Definitions" sx={{ px: 4 }} />
          <Tab value={AUTH_UPDATE_IMPORT_TEMPLATE_TABS.DRY_RUN_RESULTS} label="Dry Run Results" disabled={!dryRunResults} sx={{ px: 4 }} />
          <Tab value={AUTH_UPDATE_IMPORT_TEMPLATE_TABS.IMPORT_RESULTS} label="Import Results" disabled={!importResults} sx={{ px: 4 }} />
        </Tabs>
        <TabPanel currentTab={activeTab} value={AUTH_UPDATE_IMPORT_TEMPLATE_TABS.MAPPING_DEFINITIONS}>
          {hasUnavailableCustomFields && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {UNAVAILABLE_CUSTOM_FIELDS_TEMPLATE_WARNING}
            </Alert>
          )}
          {hasCustomFieldLoadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {CUSTOM_FIELDS_TEMPLATE_LOAD_ERROR}
            </Alert>
          )}
          <Typography variant="h6" gutterBottom>
            Template Field Mappings
          </Typography>
          <MaterialReactTable
            data={mappingRows}
            columns={templateDisplayColumns}
            getRowId={row => String(row.id || row.authFileMappingId || row.fieldId)}
            muiTableBodyRowProps={({ row }) => ({
              sx: row.original.isSectionHeader
                ? {
                  backgroundColor: '#f5f5f5',
                  '& td': {
                    borderBottom: '1px solid #d8d8d8',
                    borderTop: '1px solid #d8d8d8',
                  },
                }
                : {},
            })}
            {...authImportMappingTableProps}
          />
        </TabPanel>
        <TabPanel currentTab={activeTab} value={AUTH_UPDATE_IMPORT_TEMPLATE_TABS.DRY_RUN_RESULTS}>
          <AuthUpdateImportResults
            emptyMessage="Upload a file and run a dry run to preview the authorization updates."
            errorTitle="Dry run found"
            fieldOrder={resultFieldOrder}
            importResults={dryRunResults}
            successMessage="These authorizations will be updated when the import is submitted. Expand a row to review its changes."
          />
        </TabPanel>
        <TabPanel currentTab={activeTab} value={AUTH_UPDATE_IMPORT_TEMPLATE_TABS.IMPORT_RESULTS}>
          <AuthUpdateImportResults
            emptyMessage="Submit a successfully validated file to view import results."
            errorTitle="Import found"
            fieldOrder={resultFieldOrder}
            importResults={importResults}
            isSubmission
            successMessage="These authorizations were updated by the import submission. Expand a row to review the saved values."
          />
        </TabPanel>
      </Box>
    </>
  );
};

SelectedAuthUpdateImportTemplate.propTypes = {
  activeTab: PropTypes.string.isRequired,
  clientCustomFields: PropTypes.arrayOf(PropTypes.object),
  clientCustomFieldsStatus: PropTypes.oneOf(Object.values(CLIENT_CUSTOM_FIELDS_STATUS)),
  clients: PropTypes.arrayOf(PropTypes.object),
  dryRunResults: PropTypes.object,
  enumerations: PropTypes.object,
  importResults: PropTypes.object,
  internalUsers: PropTypes.arrayOf(PropTypes.object),
  isDryRunLoading: PropTypes.bool.isRequired,
  isImportSubmitting: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onDryRun: PropTypes.func.isRequired,
  onFileSelected: PropTypes.func.isRequired,
  onSubmitImport: PropTypes.func.isRequired,
  onTabChange: PropTypes.func.isRequired,
  selectedTemplateFile: PropTypes.object,
  template: PropTypes.object,
};

SelectedAuthUpdateImportTemplate.defaultProps = {
  clientCustomFields: [],
  clientCustomFieldsStatus: CLIENT_CUSTOM_FIELDS_STATUS.IDLE,
  clients: [],
  dryRunResults: null,
  enumerations: null,
  importResults: null,
  internalUsers: [],
  selectedTemplateFile: null,
  template: null,
};
